import React from 'react'
import { ReactComponent as CollectionIcon } from './assets/sidebar/Collections.svg'
import { ReactComponent as UserIcon } from './assets/sidebar/User.svg'
import { ReactComponent as PaymentIcon } from './assets/sidebar/Payment.svg'
import { ReactComponent as FundingIcon } from './assets/sidebar/Funding.svg'
import { ReactComponent as DeductionIcon } from './assets/sidebar/Deduction.svg'
import { EntityKind, paths } from './routes'

export type SectionKey = 'collection' | 'user' | 'simplePayment' | 'funding' | 'deduction'

// Copy comes in two lengths: `summary` for page headers and Home cards, `description` (actions only) for the landing page cards
export interface SectionAction {
    to: string
    title: string
    summary: string
    description: string
    kind: 'create' | 'update' | 'account'
}

export interface Section {
    key: SectionKey
    root: string
    // Short name for navigation tabs
    label: string
    title: string
    summary: string
    Icon: React.FC<React.SVGProps<SVGSVGElement>>
    // Landing page cards; sections without actions go straight to their lookup page
    actions: SectionAction[]
    recentKinds: EntityKind[]
}

export const sections: Section[] = [
    {
        key: 'collection',
        root: paths.collection.root,
        label: 'Collection',
        title: 'Collection',
        summary: 'Simulate incoming deposits and manage collection accounts.',
        Icon: CollectionIcon,
        actions: [
            {
                to: paths.collection.create,
                title: 'Create Collection',
                summary: 'Simulate a deposit arriving in a collection account.',
                description:
                    'Create a collection whose status depends on the Sender Name you enter. A created collection might remain Pending because of our compliance system, which lets you simulate both the Completed and Rejected statuses later in Update Collection.',
                kind: 'create',
            },
            {
                to: paths.collection.update(),
                title: 'Update Collection',
                summary: 'Approve or reject a collection held for compliance review.',
                description:
                    'Change the status of an existing collection and decide how the deposit is resolved. You can simulate both the Completed and Rejected statuses for a collection that is still Pending.',
                kind: 'update',
            },
            {
                to: paths.collection.account(),
                title: 'Update Collection Account',
                summary: 'Activate a virtual account, or disable it.',
                description:
                    'Change the status of a collection account (VA). Depending on its bank, you can simulate the Pending and Active statuses as it is set up, or disable it so it can no longer receive collections.',
                kind: 'account',
            },
        ],
        recentKinds: ['collection', 'collectionAccount'],
    },
    {
        key: 'user',
        root: paths.user(),
        label: 'User',
        title: 'User',
        summary: 'Look up a user and move them through onboarding.',
        Icon: UserIcon,
        actions: [],
        recentKinds: ['user'],
    },
    {
        key: 'simplePayment',
        root: paths.simplePayment(),
        label: 'Payment',
        title: 'Simple Payment',
        summary: 'Walk a payment through review, processing and delivery.',
        Icon: PaymentIcon,
        actions: [],
        recentKinds: ['simplePayment'],
    },
    {
        key: 'funding',
        root: paths.funding.root,
        label: 'Funding',
        title: 'Funding',
        summary: 'Add money to an account, then complete or fail it.',
        Icon: FundingIcon,
        actions: [
            {
                to: paths.funding.create,
                title: 'Create Funding',
                summary: 'Add money to an account by bank transfer, collection or refund.',
                description:
                    'Create a funding to add money to an account. A created funding might remain Pending because of our compliance system, which lets you simulate both the Completed and Failed statuses later in Update Funding.',
                kind: 'create',
            },
            {
                to: paths.funding.update(),
                title: 'Update Funding',
                summary: 'Complete or fail a pending funding.',
                description: 'Change the status of a pending funding. You can simulate both Completed, which credits the account, and Failed, which credits nothing.',
                kind: 'update',
            },
        ],
        recentKinds: ['funding'],
    },
    {
        key: 'deduction',
        root: paths.deduction.root,
        label: 'Deduction',
        title: 'Deduction',
        summary: 'Take money out of an account, then complete or fail it.',
        Icon: DeductionIcon,
        actions: [
            {
                to: paths.deduction.create,
                title: 'Create Deduction',
                summary: 'Charge a fee or adjustment against a user’s balance.',
                description:
                    'Create a deduction to take a fee or adjustment from a user’s balance. A created deduction starts in Pending, which lets you simulate both the Completed and Failed statuses later in Update Deduction.',
                kind: 'create',
            },
            {
                to: paths.deduction.update(),
                title: 'Update Deduction',
                summary: 'Complete or fail a pending deduction.',
                description:
                    'Change the status of a pending deduction. You can simulate both Completed, which takes the amount from the balance, and Failed, which takes nothing.',
                kind: 'update',
            },
        ],
        recentKinds: ['deduction'],
    },
]

export const sectionOf = (key: SectionKey): Section => sections.find((section) => section.key === key) as Section

export interface PageHeader {
    title: string
    summary: string
}

// Header for the current page: an action's own page (with or without an id) uses that action's copy,
// anything else in the section uses the section's
export function pageHeader(section: Section, pathname: string): PageHeader {
    const action = section.actions.find((each) => pathname === each.to || pathname.startsWith(`${each.to}/`))
    return action ? { title: action.title, summary: action.summary } : { title: section.title, summary: section.summary }
}

export const kindLabels: Record<EntityKind, string> = {
    collection: 'Collection',
    collectionAccount: 'Collection account',
    funding: 'Funding',
    deduction: 'Deduction',
    user: 'User',
    simplePayment: 'Simple payment',
}

export const sectionOfKind = (kind: EntityKind): Section => sections.find((section) => section.recentKinds.includes(kind)) as Section

export interface Crumb {
    label: string
    to?: string
}

const segmentLabels: Record<string, string> = {
    create: 'Create',
    update: 'Update',
    account: 'Account',
}

const decode = (segment: string) => {
    try {
        return decodeURIComponent(segment)
    } catch {
        return segment
    }
}

// Breadcrumb trail for a pathname, e.g. '/funding/update/f1' -> Home / Funding / Update / f1
export function crumbsFor(pathname: string): Crumb[] {
    const [root, ...rest] = pathname.split('/').filter(Boolean)
    const section = sections.find((each) => each.root === `/${root}`)
    if (!section) return []

    const crumbs: Crumb[] = [
        { label: 'Home', to: paths.home },
        { label: section.title, to: section.root },
    ]
    let path = section.root
    rest.forEach((segment) => {
        path = `${path}/${segment}`
        crumbs.push({ label: segmentLabels[segment] ?? decode(segment), to: path })
    })

    // The current page isn't a link
    delete crumbs[crumbs.length - 1].to
    return crumbs
}
